import { useState } from 'react';
import type { DenikZaznamDetail } from '../../api/types.js';
import { PodpisPad } from '../../components/PodpisPad.js';
import { VelkeTlacitko } from '../../components/ui.js';
import { normalizujLidi } from './lide.js';

/**
 * Kontrolní souhrn před podpisem dne (UX audit A1.2 / TOP 7b): mistr 50+
 * potřebuje kontrolu místo paměti. Dialog rekapituluje stav sekcí
 * („Práce ✓ · Lidé 6 ✓ · Fotky 0 ⚠") a u chybějících fotek VARUJE — ne
 * blokuje („opravdu podepsat bez fotky?"). Podpis je nevratný (stávající text).
 */

interface RadekSouhrnu {
  popisek: string;
  hodnota: string;
  ok: boolean;
}

export function souhrnDne(zaznam: DenikZaznamDetail): RadekSouhrnu[] {
  const lide = normalizujLidi(zaznam.lideNaStavbe);
  const pocetLidi = lide.reduce((s, l) => s + (l.pocet || 0), 0);
  const pocetFotek = zaznam.prilohy.length;
  const jsouPrace = !!zaznam.provedenePrace?.trim();
  return [
    {
      popisek: 'Provedené práce',
      hodnota: jsouPrace ? 'zapsáno' : 'chybí',
      ok: jsouPrace,
    },
    {
      popisek: 'Lidé na stavbě',
      hodnota: pocetLidi > 0 ? `${pocetLidi}` : 'nikdo',
      ok: pocetLidi > 0,
    },
    {
      popisek: 'Fotky',
      hodnota: `${pocetFotek}`,
      ok: pocetFotek > 0,
    },
    {
      popisek: 'Počasí',
      hodnota: zaznam.pocasi?.trim() ? 'vyplněno' : 'chybí',
      ok: !!zaznam.pocasi?.trim(),
    },
  ];
}

export function PodpisDialog({
  zaznam,
  jmeno,
  probiha = false,
  onPotvrdit,
  onZrusit,
}: {
  zaznam: DenikZaznamDetail;
  /** Jméno podepisujícího (přihlášený uživatel). */
  jmeno: string;
  probiha?: boolean;
  onPotvrdit: (podpis: { obrazek: string; jmeno: string }) => void;
  onZrusit: () => void;
}) {
  const souhrn = souhrnDne(zaznam);
  const bezFotek = zaznam.prilohy.length === 0;
  const [obrazek, setObrazek] = useState<string | null>(null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-overlay p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Podepsat a uzamknout den"
        className="my-8 w-full max-w-md rounded-lg bg-surface p-5 shadow-xl"
      >
        <h2 className="mb-3 text-xl font-bold">Podepsat a uzamknout den</h2>

        {/* Rekapitulace dne — kontrola místo paměti (A1.2). */}
        <ul className="mb-4 flex flex-col gap-1">
          {souhrn.map((r) => (
            <li key={r.popisek} className="flex items-center justify-between gap-3 text-base">
              <span>{r.popisek}</span>
              <span className={`font-semibold ${r.ok ? 'text-success-text' : 'text-warning-text'}`}>
                {r.hodnota} {r.ok ? '✓' : '⚠'}
              </span>
            </li>
          ))}
        </ul>

        {bezFotek ? (
          <p
            role="status"
            className="mb-4 rounded-md border border-warning-border bg-warning-bg p-3 text-base text-warning-text"
          >
            Žádná fotka — opravdu podepsat bez fotky?
          </p>
        ) : null}

        <p className="mb-4 text-base text-text-secondary">
          Podpis den uzamkne — nelze vzít zpět. Záznam už nepůjde měnit (nikým); oprava pak jen
          opravným záznamem.
        </p>

        {/* Vlastnoruční podpis (prst/myš) — bez něj nelze potvrdit. */}
        <div className="mb-4">
          <PodpisPad onChange={setObrazek} />
          <p className="mt-1 text-sm text-text-secondary">Podepisuje: {jmeno}</p>
        </div>

        <div className="flex flex-col gap-3">
          <VelkeTlacitko
            varianta="podpis"
            disabled={probiha || !obrazek}
            onClick={() => obrazek && onPotvrdit({ obrazek, jmeno })}
          >
            {probiha ? 'Odesílám…' : 'Podepsat — rozumím, je to trvalé'}
          </VelkeTlacitko>
          <VelkeTlacitko varianta="secondary" disabled={probiha} onClick={onZrusit}>
            Zrušit
          </VelkeTlacitko>
        </div>
      </div>
    </div>
  );
}
